import { Component, OnInit } from '@angular/core';
import { Router } from "@angular/router";
import { IdService } from "../../services/id/id.service";
import { PermissionsService } from "../../services/permissions/permissions.service";
import { SharedService } from "../../services/shared/shared.service";
import Constants from "../../shared/constants";
import * as Cookies from 'js-cookie';
import { environment } from "../../../environments/environment";
import { JwtHelperService } from "@auth0/angular-jwt";
import { TranslateService } from "@xref/ngx";

interface DataRegion {
    key: string;
    value: string;
}

interface OrgDetails {
    name: string;
    industry: string;
    org_size: string;
    region: string;
    email: string;
    language_locale: string;
    timezone: string;
}

@Component({
    selector: 'xlog-org-creation',
    templateUrl: './org-creation.component.pug',
    styleUrls: ['./org-creation.component.scss'],
    standalone: false
})
export class OrgCreationComponent implements OnInit {
    // This is P4 from the signup flow (org details + DC).
    ORG_SIZE_PLACEHOLDER = this.translateService.instant('signUpText.orgCreation.orgSizePlaceholder');
    textValueOrgName = '';
    email = '';
    dropDownOrgSizePlaceholder = this.ORG_SIZE_PLACEHOLDER;
    orgIndustry = '';
    orgSize = '';
    dataRegion = '';
    orgDetails: OrgDetails;
    loaded = true;
    showBanner = false;
    dataRegions: DataRegion[] = [];
    redirectionFromUserLoggedInPage = false;
    compWindow: any;

    ff_ent_10781_sign_up = false;

    readonly orgSizes = Constants.ORG_SIZES;
    protected readonly Constants = Constants;

    protected _translatedIndustries: any[] = [];
    protected _translatedDataRegions: any[] = [];
    private readonly allDataRegions: DataRegion[] = [
        { key: 'us', value: 'United States' },
        { key: 'au', value: 'Australia' },
        { key: 'ca', value: 'Canada' },
        { key: 'eu', value: 'Europe' }
    ];

    constructor(
        private router: Router,
        private idService: IdService,
        private sharedService: SharedService,
        private featureFlagService: PermissionsService,
        private jwtHelper: JwtHelperService,
        private translateService: TranslateService
    ) {
    }

    ngOnInit(): void {
        this.ff_ent_10781_sign_up = this.featureFlagService.isFeatureActivated('ent-10781-signup-improvements');
        this.updateTranslatedIndustries();
        this.updateTranslatedDataRegions();

        this.translateService.onLangChange.subscribe(() => {
            this.updateTranslatedIndustries();
            this.updateTranslatedDataRegions();
            this.initializeDataRegions();
            this.ORG_SIZE_PLACEHOLDER = this.translateService.instant('signUpText.orgCreation.orgSizePlaceholder');
        });

        if (this.ff_ent_10781_sign_up) {
            this.fetchSignupOrgDetails();
        }

        // Temporary style adjustment to match Figma design - will be removed when Component Library V2.0 is released
        const industryDropdown = document.querySelector('.input-set:nth-child(2) .white-dropdown .select-dropdown');
        const sizeDropdown = document.querySelector('.input-set:nth-child(3) .white-dropdown .select-dropdown');
        const regionDropdown = document.querySelector('.input-set:nth-child(4) .white-dropdown .select-dropdown');

        if (industryDropdown) industryDropdown.setAttribute('data-dropdown-type', 'industry');
        if (sizeDropdown) sizeDropdown.setAttribute('data-dropdown-type', 'size');
        if (regionDropdown) regionDropdown.setAttribute('data-dropdown-type', 'region');

        const dropdownButtons = document.querySelectorAll('.white-dropdown .select-dropdown');
        dropdownButtons.forEach(button => {
            button.classList.add('placeholder-text');
        });

        if (!this.ff_ent_10781_sign_up) {
            const orgSize = Cookies.get('org_size') || this.orgSize;
            if (orgSize) {
                this.orgSize = this.orgSizes.find(size => size.value === orgSize)?.key || '';
                this.dropDownOrgSizePlaceholder = this.orgSize || '';
                this.removePlaceholderTextClass('size');
            }
        }

        this.compWindow = window;
        const navigation = localStorage.getItem('previousUrl');
        if (navigation && navigation.includes('/user-logged-in')) {
            this.redirectionFromUserLoggedInPage = true;
        }
        localStorage.removeItem('previousUrl');
        this.initializeDataRegions();
    }

    dropDownOrgSizeOptionSelected(orgSize): void {
        this.orgSize = orgSize.value;
        this.removePlaceholderTextClass('size');
    }

    dropDownOrgIndustryOptionSelected(orgIndustry: { key: number }): void {
        this.orgIndustry = Constants.EN_ORG_INDUSTRIES[orgIndustry.key];
        this.removePlaceholderTextClass('industry');
    }

    dropDownDataRegionOptionSelected(dataRegion: DataRegion): void {
        this.dataRegion = dataRegion.key;
        this.removePlaceholderTextClass('region');
    }

    removePlaceholderTextClass(dropdown_type: string): void {
        // Temporary style adjustment to match Figma design - will be removed when Component Library V2.0 is released
        const button = document.querySelector('[data-dropdown-type=' + dropdown_type + ']');
        if (button) button.classList.remove('placeholder-text');
    }

    onSubmit(): void {
        if (!this.textValueOrgName || this.orgIndustry === '' || this.dataRegion === '' || this.orgSize === '') {
            return;
        }
        this.loaded = false;
        const userData = this.sharedService.getUserData();
        const locale = Cookies.get(Constants.LOCALE_TOKEN) || 'en-au';
        let urlLocale = locale;
        const omniLocales = Constants.OMNI_LOCALES;
        if (!omniLocales.includes(urlLocale)) {
            urlLocale = 'en-au';
        }
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        this.orgDetails = {
            name: this.textValueOrgName,
            industry: this.orgIndustry,
            org_size: this.orgSize,
            region: this.dataRegion,
            email: userData.email,
            language_locale: locale,
            timezone
        };

        this.idService.createOrganisation(userData?.token, this.orgDetails).subscribe({
            next: (orgResponse) => {
                userData.assigned_org_uuid = orgResponse.data.global_org_uuid;
                this.idService.createUser(userData.token, userData).subscribe({
                    next: () => {
                        const tokenData = {
                            email: userData.email,
                            password: userData.password,
                            organisation_uuid: userData.assigned_org_uuid
                        };
                        if (this.redirectionFromUserLoggedInPage) {
                            delete tokenData.password;
                        }
                        this.idService.getJwtAccessToken(tokenData).subscribe({
                            next: (tokenResponse) => {
                                this.loaded = true;
                                this.idService.removeToken();
                                // @ts-ignore
                                this.idService.setTokens(tokenResponse.data);
                                // @ts-ignore
                                Cookies.set('org_size', this.orgSize, { domain: environment.DOMAIN });
                                Cookies.set('xref-first-time-login', 'true', { domain: environment.DOMAIN });
                                Cookies.set('xref-onboarding-video-guides', 'true', { domain: environment.DOMAIN });
                                if (this.ff_ent_10781_sign_up) {
                                    this.compWindow.location.href = environment.OMNI_URL + urlLocale.toUpperCase();
                                } else {
                                    this.compWindow.location.href = environment.OMNI_URL + urlLocale.toUpperCase() + '/billing';
                                }
                            },
                            error: () => {
                                this.showAlertBanner();
                                this.loaded = true;
                            }
                        });
                    },
                    error: (err) => {
                        console.error('User creation failed:', err);
                        this.loaded = true;
                        this.showAlertBanner();
                    }
                });
            },
            error: (error) => {
                if (error?.error?.data?.global_org_uuid) {
                    const deleteOrgData = {
                        name: this.textValueOrgName,
                        region: this.dataRegion,
                        global_org_uuid: error?.error?.data?.global_org_uuid
                    };

                    this.idService.rollBackOrgAndUser(userData?.token, deleteOrgData).subscribe({
                        next: () => {
                            this.showAlertBanner();
                            this.loaded = true;
                            console.log('Rollback org successful');
                        },
                        error: (rollbackError) => {
                            this.showAlertBanner();
                            this.loaded = true;
                            console.error('Rollback org failed:', rollbackError);
                        }
                    });
                } else if (error.status === 401) {
                    window.location.href = '/signup/expired';
                    localStorage.removeItem("userData");
                } else {
                    console.error('Organisation creation failed:', error);
                    this.loaded = true;
                    this.showAlertBanner();
                }
            }
        });
    }

    showAlertBanner() {
        this.showBanner = true;
        setTimeout(() => {
            this.showBanner = false;
        }, 5000);  // Hide after 5 seconds
    }

    private initializeDataRegions(): void {
        const currentUrl = window.location.origin;

        if (currentUrl.includes('dev.xref.com') || currentUrl.includes('localhost')) {
            // Dev environment - only Australia
            this.dataRegions = this._translatedDataRegions.filter(region => region.key === 'au');
        } else if (currentUrl.includes('sandbox.xref.com')) {
            // Sandbox environment - Australia and Europe
            this.dataRegions = this._translatedDataRegions.filter(region =>
                ['au', 'eu'].includes(region.key)
            );
        } else if (currentUrl.includes('xref.com')) {
            // Production environment - all regions
            this.dataRegions = [...this._translatedDataRegions];
        } else {
            // Default fallback - only Australia
            console.warn('Unknown environment detected, defaulting to Australia only');
            this.dataRegions = this._translatedDataRegions.filter(region => region.key === 'au');
        }
    }

    private updateTranslatedIndustries() {
        this._translatedIndustries = Constants.ORG_INDUSTRIES
            .map(industry => ({
                key: industry.key,
                value: this.translateService.instant(industry.value)
            }))
            .sort((a, b) => a.value.localeCompare(b.value));
    }

    private updateTranslatedDataRegions() {
        this._translatedDataRegions = Constants.DATA_REGIONS.map(region => ({
            key: region.key,
            value: this.translateService.instant(region.value)
        }));
    }

    private fetchSignupOrgDetails(): void {
        const userData = this.sharedService.getUserData();
        this.idService.getSignupOrganisationDetails(userData.token).subscribe({
            next: (response) => {
                const data = response?.data || {};
                this.textValueOrgName = data.org_name || '';
                this.orgSize = data.org_size || '';
                this.orgIndustry = data.industry || '';
                this.dataRegion = data.region || '';
                if (data.org_size) {
                    this.dropDownOrgSizePlaceholder = this.orgSize;
                    this.removePlaceholderTextClass('size');
                }
            },
            error: (err) => {
                console.error('Failed to fetch signup organisation details:', err);
            }
        });
    }
}
